// ─────────────────────────────────────────────────────────────────────────────
// useUserRules
//
// Loads and manages the current user's trading rules for a specific ACCOUNT.
// All operations are scoped to (userId, accountId).
//
// Uses authFetch (which wraps ensureValidToken + authHeaders + fetch)
// so token refresh is handled automatically before each request.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useCallback, useState } from "react";
import { SUPABASE_URL, authFetch } from "../lib/supabase";
import { defaultParamsFor, RULE_TYPES, RULE_PRESETS } from "../lib/rules";

export function useUserRules(userId, accountId) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId || !accountId || !SUPABASE_URL) {
      setRules([]);
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_rules?user_id=eq.${userId}&account_id=eq.${accountId}&order=created_at.asc&select=*`
      );
      if (!res.ok) {
        const txt = await res.text();
        console.error("useUserRules fetch:", res.status, txt);
        setError(txt);
        setRules([]);
      } else {
        const data = await res.json();
        setRules(Array.isArray(data) ? data : []);
        setError(null);
      }
    } catch (e) {
      console.error("useUserRules error:", e);
      setError(String(e));
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [userId, accountId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  // ── CRUD ────────────────────────────────────────────────────────────

  const addRule = useCallback(async ({ rule_type, parameters, severity = "warn", custom_label = null, preset_origin = null }) => {
    if (!userId || !accountId) return null;
    if (!RULE_TYPES[rule_type]) {
      console.error(`Unknown rule_type: ${rule_type}`);
      return null;
    }
    const body = {
      user_id: userId,
      account_id: accountId,
      rule_type,
      parameters: parameters || defaultParamsFor(rule_type),
      severity,
      custom_label,
      preset_origin,
      enabled: true,
    };
    try {
      const res = await authFetch(`${SUPABASE_URL}/rest/v1/user_rules`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        console.error("addRule failed:", res.status, await res.text());
        return null;
      }
      const data = await res.json();
      const created = Array.isArray(data) ? data[0] : data;
      await refresh();
      return created;
    } catch (e) {
      console.error("addRule error:", e);
      return null;
    }
  }, [userId, accountId, refresh]);

  const updateRule = useCallback(async (ruleId, updates) => {
    if (!userId || !ruleId) return null;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_rules?id=eq.${ruleId}`,
        {
          method: "PATCH",
          headers: { Prefer: "return=representation" },
          body: JSON.stringify(updates),
        }
      );
      if (!res.ok) {
        console.error("updateRule failed:", res.status, await res.text());
        return null;
      }
      await refresh();
      return true;
    } catch (e) {
      console.error("updateRule error:", e);
      return null;
    }
  }, [userId, refresh]);

  const deleteRule = useCallback(async (ruleId) => {
    if (!userId || !ruleId) return false;
    try {
      const res = await authFetch(
        `${SUPABASE_URL}/rest/v1/user_rules?id=eq.${ruleId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        console.error("deleteRule failed:", res.status, await res.text());
        return false;
      }
      await refresh();
      return true;
    } catch (e) {
      console.error("deleteRule error:", e);
      return false;
    }
  }, [userId, refresh]);

  const toggleRule = useCallback((ruleId, enabled) => {
    return updateRule(ruleId, { enabled });
  }, [updateRule]);

  /**
   * Shared duplicate-guarded insert logic used by both built-in presets
   * (RULE_PRESETS, looked up by id) and user-created custom presets
   * (arbitrary rule lists saved by the user, applied by value).
   */
  const applyRuleList = useCallback(async (ruleList, originId) => {
    const added = [];
    const skipped = [];

    for (const r of ruleList) {
      const def = RULE_TYPES[r.rule_type];
      const allowsMultiple = def?.multiple === true;

      const isDuplicate = rules.some(existing => {
        if (existing.rule_type !== r.rule_type) return false;
        if (!allowsMultiple) return true;
        return JSON.stringify(existing.parameters) === JSON.stringify(r.parameters);
      });

      if (isDuplicate) {
        skipped.push(r.rule_type);
        continue;
      }

      const created = await addRule({
        rule_type: r.rule_type,
        parameters: r.parameters,
        severity: r.severity || "warn",
        preset_origin: originId,
      });
      if (created) added.push(r.rule_type);
      else skipped.push(r.rule_type);
    }

    return { ok: skipped.length === 0, added, skipped };
  }, [addRule, rules]);

  /**
   * Apply a built-in preset bundle (from RULE_PRESETS) to the current account.
   * Inserts each rule from the preset. Does NOT remove existing rules.
   *
   * Duplicate guard: if a rule type doesn't allow multiples and already
   * exists on this account, that rule is skipped. Returns an object
   * describing what was added vs skipped so the UI can inform the user.
   */
  const applyPreset = useCallback(async (presetId) => {
    const preset = RULE_PRESETS[presetId];
    if (!preset) {
      console.error(`Unknown preset: ${presetId}`);
      return { ok: false, added: [], skipped: [] };
    }
    return applyRuleList(preset.rules, presetId);
  }, [applyRuleList]);

  /**
   * Apply a user-created custom preset (from useCustomPresets) to the
   * current account. Same duplicate-guard behavior as applyPreset, but
   * takes the preset object by value instead of looking it up by id,
   * since custom presets aren't part of the static RULE_PRESETS catalog.
   */
  const applyCustomPreset = useCallback(async (preset) => {
    if (!preset?.rules?.length) {
      return { ok: false, added: [], skipped: [] };
    }
    return applyRuleList(preset.rules, `custom:${preset.id}`);
  }, [applyRuleList]);

  return {
    rules,
    loading,
    error,
    refresh,
    addRule,
    updateRule,
    deleteRule,
    toggleRule,
    applyPreset,
    applyCustomPreset,
  };
}
